import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined"
import { Card, Grid, IconButton } from "@mui/material"
import { Button, Menu, MenuItem, Skeleton, Tag, Typography } from "@synapse-analytics/synapse-ui"

import { KonanAvatarComponent } from "../../components/core/KonanAvatar"
import type { RootState } from "../../store/ReduxStore"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import type { ScriptCardProps } from "./Interfaces"
import { StatusColorMap, StatusTextMap } from "./Interfaces"

import styles from "./Scripts.module.scss"

/**
 * Renders a script card component with details about a specific script. It includes interactive elements
 * such as edit, delete, and logs. This component is integrated with several dialogs for different
 * actions (view logs, edit, delete) and uses mutations for script deletion.
 *
 * @param {Script} script script data.
 * @returns The `ReactElement` that represents the script card.
 */
export function ScriptCard({
  script,
  MenuOptions,
  onLogsDialogClick,
  onNameClick,
}: Readonly<ScriptCardProps>): React.ReactElement {
  const { created_at, created_by, name, is_draft, status } = script

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve script", permissions.konan, flattenedKonanPermissions)

  const { t } = useTranslation(["scripts"])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Card className="card-box-shadow">
      <Grid container direction="column" className={styles.card}>
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="flex-start">
          <Grid item container gap={0.5}>
            <Grid item>
              <Tag size="small" color={StatusColorMap[status as keyof typeof StatusColorMap]}>
                {t(`types.StatusTextMap.${StatusTextMap[status as keyof typeof StatusTextMap]}`)}
              </Tag>
            </Grid>

            <Grid item>
              {is_draft && (
                <Tag color="blue" size="small">
                  {t("scriptCard.tags.draft")}
                </Tag>
              )}
            </Grid>
          </Grid>

          {/* Menu  */}

          <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-start">
            <IconButton
              style={{ marginTop: "-5px" }}
              onClick={(e) => setAnchorEl(e?.currentTarget)}
              aria-label="settings"
              size="small"
            >
              <MoreHorizIcon sx={{ color: "var(--base-text-2)" }} />
            </IconButton>
            <Menu
              id="simple-menu"
              onClose={() => setAnchorEl(null)}
              anchorEl={anchorEl}
              menuMaxContent
              open={Boolean(anchorEl)}
            >
              {MenuOptions?.map((option) => (
                <MenuItem
                  onClick={() => {
                    option.onClick()
                    setAnchorEl(null)
                  }}
                  key={option.title}
                >
                  <Typography
                    className={styles.optionsMenuItem}
                    variant="a"
                    color={option.variant ?? "gray"}
                    variantColor={2}
                  >
                    {option.title}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Grid>
        </Grid>

        {/* Name and draft status */}
        <Grid item container flexWrap="nowrap" xs={12} gap={1} alignItems="center" justifyContent="flex-start">
          <Typography
            variant="h2-bold"
            noWrap
            onClick={onNameClick}
            className={styles.scriptName}
            color="important"
            variantColor={2}
          >
            {name}
          </Typography>
        </Grid>

        {/* Created by and logs row */}
        <Grid container item xs={12} justifyContent="space-between" wrap="nowrap" mt={0.5}>
          <Grid
            container
            item
            spacing={1}
            alignItems="center"
            display="flex"
            flexWrap="nowrap"
            // 42px = button width
            maxWidth={"calc(100% - 42px)"}
          >
            <KonanAvatarComponent createdBy={created_by} title={created_by} createdAt={created_at} />
          </Grid>

          <Grid item display="flex" alignSelf="flex-end" justifySelf="flex-end">
            {canShowDetails && (
              <Button onClick={onLogsDialogClick} size={"small"} variant="secondary">
                <TerminalOutlinedIcon sx={{ fontSize: "16px" }} />
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}

/**
 * Loading card for script card
 * @returns {React.ReactElement}
 */
export function ScriptLoadingCard(): React.ReactElement {
  return (
    <Card className={"card-box-shadow"}>
      <Grid container direction="column" className={styles.card}>
        <Grid item xs={3} mb={0.5}>
          <Skeleton variant="rectangular" height={19} width={"20%"} />
        </Grid>

        <Grid item mt={1} xs={12}>
          <Skeleton variant="rectangular" height={19} width={"60%"} />
        </Grid>

        <Grid item xs={12} container flexWrap="nowrap" spacing={1} alignItems="center">
          <Grid item mt={1}>
            <Skeleton variant="circular" width={32} height={32} />
          </Grid>

          <Grid direction="column" container item mt={1}>
            <Grid item xs={6} my={1}>
              <Skeleton variant="rectangular" height={10} width={"80%"} />
            </Grid>
            <Grid item xs={3} mb={0.5}>
              <Skeleton variant="rectangular" height={10} width={"30%"} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
